import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { CameraService } from '../services/camera.service';

declare const cv: any; // OpenCV.js global

@Component({
  selector: 'app-camera-capture',
  templateUrl: './camera-capture.component.html',
  styleUrls: ['./camera-capture.component.scss'],
})
export class CameraCaptureComponent implements OnInit, OnDestroy {
  @ViewChild('video') videoElement!: ElementRef<HTMLVideoElement>;
  @ViewChild('overlay') canvasElement!: ElementRef<HTMLCanvasElement>;
  
  @Input() label: string = 'Capture Document';
  @Input() facingMode: 'user' | 'environment' = 'environment';
  
  @Output() photoCaptured = new EventEmitter<File>();
  @Output() cancelled = new EventEmitter<void>();

  isCameraActive: boolean = false;
  isCapturing: boolean = false;
  error: string | null = null;
  currentFacingMode: 'user' | 'environment' = 'environment';
  hasMultipleCameras: boolean = false;
  isInitializing: boolean = false;
  
  // Document detection
  private animationFrameId: number | null = null;
  private isFrameReady: boolean = false;
  private documentDetected: boolean = false;
  private openCVReady: boolean = false;

  constructor(
    private cameraService: CameraService,
    private translate: TranslateService
  ) {}

  async ngOnInit() {
    this.currentFacingMode = this.facingMode;
    
    // Verifica se tem múltiplas câmeras
    const capabilities = await this.cameraService.checkCameraAvailability();
    this.hasMultipleCameras = capabilities.cameras.length > 1;
    
    // Auto-start camera after a brief delay to ensure view is ready
    this.isInitializing = true;
    setTimeout(() => {
      this.startCamera();
    }, 300);
  }

  ngOnDestroy() {
    this.stopCamera();
    this.stopFrameProcessing();
  }

  /**
   * Inicializa OpenCV.js para detecção de documentos
   */
  private initializeOpenCV(): void {
    if (typeof cv === 'undefined') {
      console.warn('OpenCV.js not loaded');
      return;
    }

    if (cv.getBuildInformation) {
      this.openCVReady = true;
      console.log('OpenCV.js initialized');
    }
  }

  /**
   * Inicia a câmera
   */
  async startCamera() {
    try {
      this.error = null;
      this.isInitializing = true;
      
      // Inicializa OpenCV se ainda não estiver
      if (!this.openCVReady) {
        this.initializeOpenCV();
      }
      
      console.log('Starting camera with facing mode:', this.currentFacingMode);
      
      const stream = await this.cameraService.startCamera(
        this.currentFacingMode
      );

      console.log('Camera stream obtained:', stream);

      // Aguarda o videoElement estar disponível com retry
      const maxRetries = 10;
      let retries = 0;
      
      const attachStream = () => {
        if (this.videoElement && this.videoElement.nativeElement) {
          console.log('Attaching stream to video element');
          this.videoElement.nativeElement.srcObject = stream;
          this.isCameraActive = true;
          this.isInitializing = false;
          
          // Inicia processamento de frames para detecção de documento
          this.startFrameProcessing();
          
          // Force video to play
          this.videoElement.nativeElement.play().catch(err => {
            console.error('Error playing video:', err);
          });
        } else if (retries < maxRetries) {
          retries++;
          console.log(`Video element not ready, retry ${retries}/${maxRetries}`);
          setTimeout(attachStream, 100);
        } else {
          console.error('Video element not available after retries');
          this.error = this.translate.instant('FailedToInitializeVideo');
          this.isInitializing = false;
        }
      };

      attachStream();
    } catch (error: any) {
      this.error = this.translate.instant(error.message) || this.translate.instant('FailedToStartCamera');
      this.isInitializing = false;
      console.error('Camera error:', error);
    }
  }

  /**
   * Para a câmera
   */
  stopCamera() {
    this.stopFrameProcessing();
    this.cameraService.stopCamera();
    if (this.videoElement && this.videoElement.nativeElement) {
      this.videoElement.nativeElement.srcObject = null;
    }
    this.isCameraActive = false;
  }

  /**
   * Detecta documento e desenha overlay
   */
  private detectDocumentAndDrawOverlay(): void {
    if (!this.openCVReady || !this.videoElement?.nativeElement || !this.canvasElement?.nativeElement) {
      return;
    }

    const video = this.videoElement.nativeElement;
    const canvas = this.canvasElement.nativeElement;
    const ctx = canvas.getContext('2d');

    if (!ctx) return;

    // Sincroniza dimensões do canvas com o vídeo
    if (canvas.width !== video.offsetWidth || canvas.height !== video.offsetHeight) {
      canvas.width = video.offsetWidth;
      canvas.height = video.offsetHeight;
    }

    // Desenha o frame do vídeo no canvas
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Cria um canvas interno para processamento OpenCV
    const processingCanvas = document.createElement('canvas');
    processingCanvas.width = canvas.width;
    processingCanvas.height = canvas.height;
    const processingCtx = processingCanvas.getContext('2d');
    if (!processingCtx) return;
    
    processingCtx.drawImage(video, 0, 0, canvas.width, canvas.height);

    try {
      // Lê o frame do canvas de processamento para OpenCV
      const src = cv.imread(processingCanvas);
      const gray = new cv.Mat();
      const blur = new cv.Mat();
      const thresh = new cv.Mat();
      const contours = new cv.MatVector();
      const hierarchy = new cv.Mat();

      // Converte para escala de cinza
      cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);

      // Aplica blur maior para reduzir ruído
      cv.GaussianBlur(gray, blur, new cv.Size(7, 7), 0);

      // Aplica Canny para detectar bordas
      cv.Canny(blur, thresh, 50, 150);

      // Encontra contornos
      cv.findContours(thresh, contours, hierarchy, cv.RETR_EXTERNAL, cv.CHAIN_APPROX_SIMPLE);

      // Parâmetros de detecção
      const minArea = (canvas.width * canvas.height) * 0.08;
      const maxArea = (canvas.width * canvas.height) * 0.92;
      const canvasArea = canvas.width * canvas.height;

      let bestContourPoints: number[] = [];
      let bestArea = 0;
      let isDocumentAligned = false;

      // Procura o maior contorno que pareça um documento
      for (let i = 0; i < contours.size(); i++) {
        const cnt = contours.get(i);
        const area = cv.contourArea(cnt);

        // Filtra por área
        if (area < minArea || area > maxArea) {
          cnt.delete();
          continue;
        }

        // Verifica se é um polígono (aproximadamente retângulo)
        const approx = new cv.Mat();
        const peri = cv.arcLength(cnt, true);
        cv.approxPolyDP(cnt, approx, 0.015 * peri, true);

        // Documentos devem ter 4 lados
        if (approx.rows === 4) {
          if (area > bestArea) {
            bestArea = area;
            
            // Copia os pontos do contorno ANTES de deletar
            bestContourPoints = [];
            const pts = approx.data32S;
            for (let j = 0; j < approx.rows; j++) {
              bestContourPoints.push(pts[j * 2]);
              bestContourPoints.push(pts[j * 2 + 1]);
            }

            // Valida alinhamento
            const areaRatio = bestArea / canvasArea;
            isDocumentAligned = areaRatio > 0.12 && areaRatio < 0.88;
          }
        }

        approx.delete();
        cnt.delete();
      }

      // Limpa o canvas antes de desenhar
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Desenha o overlay
      let overlayColor = '#ff6b6b'; // Vermelho por padrão

      if (bestContourPoints.length > 0 && bestArea > 0) {
        if (isDocumentAligned) {
          overlayColor = '#51cf66'; // Verde quando alinhado
          this.documentDetected = true;
        } else {
          this.documentDetected = false;
        }

        // Expande o polígono para cobrir melhor o documento (30% x e 25% y)
        let expandedPoints = bestContourPoints.slice();
        
        // Calcula o centróide
        let cx = 0, cy = 0;
        for (let i = 0; i < bestContourPoints.length; i += 2) {
          cx += bestContourPoints[i];
          cy += bestContourPoints[i + 1];
        }
        cx /= (bestContourPoints.length / 2);
        cy /= (bestContourPoints.length / 2);

        // Expande cada ponto afastando do centróide
        expandedPoints = [];
        for (let i = 0; i < bestContourPoints.length; i += 2) {
          const px = bestContourPoints[i];
          const py = bestContourPoints[i + 1];
          
          const dx = px - cx;
          const dy = py - cy;
          
          const newX = Math.max(0, Math.min(canvas.width, px + dx * 0.30));
          const newY = Math.max(0, Math.min(canvas.height, py + dy * 0.25));
          
          expandedPoints.push(newX);
          expandedPoints.push(newY);
        }

        // Desenha o polígono do documento
        ctx.strokeStyle = overlayColor;
        ctx.lineWidth = 4;
        ctx.fillStyle = overlayColor;

        ctx.beginPath();
        for (let i = 0; i < expandedPoints.length; i += 2) {
          const x = expandedPoints[i];
          const y = expandedPoints[i + 1];
          if (i === 0) {
            ctx.moveTo(x, y);
          } else {
            ctx.lineTo(x, y);
          }
        }
        ctx.closePath();
        ctx.globalAlpha = 0.15;
        ctx.fill();
        ctx.globalAlpha = 1;
        ctx.stroke();
      }

      // Libera memória
      src.delete();
      gray.delete();
      blur.delete();
      thresh.delete();
      contours.delete();
      hierarchy.delete();
    } catch (error) {
      console.error('OpenCV processing error:', error);
    }
  }

  /**
   * Loop de processamento de frames
   */
  private startFrameProcessing(): void {
    if (this.animationFrameId !== null) return;

    const processFrame = () => {
      this.detectDocumentAndDrawOverlay();
      this.animationFrameId = requestAnimationFrame(processFrame);
    };

    this.animationFrameId = requestAnimationFrame(processFrame);
  }

  /**
   * Para o processamento de frames
   */
  private stopFrameProcessing(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    // Limpa o canvas
    if (this.canvasElement?.nativeElement) {
      const ctx = this.canvasElement.nativeElement.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, this.canvasElement.nativeElement.width, this.canvasElement.nativeElement.height);
      }
    }
  }

  /**
   * Captura a foto
   */
  async capturePhoto() {
    if (!this.videoElement || !this.videoElement.nativeElement) {
      this.error = this.translate.instant('VideoElementNotReady');
      return;
    }

    try {
      this.isCapturing = true;
      this.error = null;

      const blob = await this.cameraService.capturePhoto(
        this.videoElement.nativeElement
      );

      // Converte blob para File
      const timestamp = new Date().getTime();
      const file = new File([blob], `document-${timestamp}.jpg`, {
        type: 'image/jpeg',
      });

      this.photoCaptured.emit(file);
      this.stopCamera();
    } catch (error: any) {
      this.error = this.translate.instant('FailedToCapturePhoto');
      console.error('Capture error:', error);
    } finally {
      this.isCapturing = false;
    }
  }

  /**
   * Troca entre câmera frontal e traseira
   */
  async switchCamera() {
    try {
      this.error = null;
      const stream = await this.cameraService.switchCamera(
        this.currentFacingMode
      );
      this.currentFacingMode =
        this.currentFacingMode === 'user' ? 'environment' : 'user';

      if (this.videoElement && this.videoElement.nativeElement) {
        this.videoElement.nativeElement.srcObject = stream;
      }
    } catch (error: any) {
      this.error = this.translate.instant('FailedToSwitchCamera');
      console.error('Switch camera error:', error);
    }
  }

  /**
   * Cancela a captura
   */
  cancel() {
    this.stopCamera();
    this.cancelled.emit();
  }

  /**
   * Obtém o ícone da câmera atual
   */
  getCameraIcon(): string {
    return this.currentFacingMode === 'user' ? 'camera_front' : 'camera_rear';
  }
}
